import Icon, { IconType } from 'components/Icon';
import DeletedFrameLightbox from 'components/Widget/DeletedFrameLightbox';
import Moon from 'components/Widget/Moon';
import { Tooltip } from 'components/Widget/Tooltip';
import { FrameStatus, FrameType } from 'dragonmouse-shared/interfaces';
import { useFrameMoonProgress } from 'hooks/useFrameMoonProgress';
import { useImageLoading } from 'hooks/useImageLoading';
import { MenuItemAction } from 'menu/MenuItem';
import { useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { PlacesType } from 'react-tooltip';
import { selectFrame } from 'redux/db/reducers';
import { getFrameDisplayBounds } from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setCenteredFrameId,
	setCenterFramesForChatId,
	setSelected,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { frameImageUrl, frameImageUrlById } from 'utils';
import SmallIconButton, { SmallIconButtonState } from '../Button/SmallIconButton';
import * as S from './styles';

export enum ThumbnailState {
	REGULAR,
	DISABLED,
}

export enum ThumbnailSize {
	RIGHT_PANEL = 104,
	CHAT = 84,
	MODAL = 80,
}

interface ThumbnailProps {
	frameId: string;
	tooltip?: string;
	tooltipPlacement?: PlacesType;
	onDelete?: () => void;
	size: ThumbnailSize;

	// the default on-click behavior is to center the frame on the canvas
	// if selectOnClick is true, the frame is also selected on click
	selectOnClick?: boolean;

	// if onClick is set, it will override the default on-click behavior (it will be called instead of the default)
	onClick?: () => void;

	// chat conversation: split layout with a bottom Preview bar
	showPreviewAction?: boolean;
}

// HACK
// HACK
// it is expected that the thumbnail's parent element will have a data-frame-id attribute
// this is currently depended upon by Conversation (but there is no way to really document
// or enforce it in code). So for now hopefully this comment will suffice.
// HACK
// HACK
const Thumbnail = ({
	frameId,
	onDelete,
	tooltip,
	tooltipPlacement,
	size,
	selectOnClick,
	onClick,
	showPreviewAction,
}: ThumbnailProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [tooltipId] = useState(String(Math.random()));
	const [previewTooltipId] = useState(String(Math.random()));
	const [isHovered, setIsHovered] = useState(false);
	const [isDeletedLightboxOpen, setIsDeletedLightboxOpen] = useState(false);
	const ref = useRef(null);

	const { frame, isMobile } = useSelector(
		(r: RootReducer) => ({
			// make sure this includes deleted frames because the chat can show thumbnails of frames that have been deleted from the canvas
			frame: selectFrame(r.db, frameId),
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);

	// When the frame isn't in Redux (e.g. DS chat opened without the scene), fall back to
	// frameImageUrlById which hits /frame/:id/image. The backend resolves veHash etc. itself.
	// On local dev this can briefly show a loading/broken image while waiting for stream to
	// deliver a newly-created frame to Redux — this is transient and self-corrects.
	const imageSrc = frame ? frameImageUrl(frame) : frameImageUrlById(frameId);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');
	const { progress: moonProgress, isIndeterminate } = useFrameMoonProgress(frame, isImageLoading);
	const isSplitLayout = showPreviewAction && size === ThumbnailSize.CHAT;
	// In the split layout the image and the preview bar are two distinct hit
	// targets; teach that via the tooltips rather than the frame title (which
	// nobody reads). Outside split layout, fall back to the title.
	const tooltipString = isSplitLayout ? 'Click to focus' : tooltip ?? frame?.title;

	const canPreview = !frame || frame.metadata?.type !== FrameType.IMPORT;
	const isReadyForPreview =
		!!frame &&
		!isImageLoading &&
		frame.metadata?.status !== FrameStatus.PROCESSING &&
		frame.metadata?.status !== FrameStatus.ERROR;
	const showPreviewBar = isSplitLayout && canPreview;

	const previewIconSize = 10;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onClickThumbnail = () => {
		if (onClick) {
			onClick();
			return;
		}
		if (!frame) return;

		// A deleted frame has nowhere on the canvas to point at, so the click
		// opens it for a decision instead of navigating.
		if (frame.deletedAt) {
			setIsDeletedLightboxOpen(true);
			return;
		}

		if (isMobile) {
			dispatch(setCenteredFrameId(frame.id));
			return;
		}

		dispatch(setCenterFramesForChatId(null));
		dispatch(
			centerCanvasAroundSceneBoundingBox({
				boundingBox: getFrameDisplayBounds(frame),
				animated: true,
				frameId: frame.id,
			})
		);
		if (selectOnClick) {
			dispatch(setSelected([new Pick(frame.id)]));
		}
	};

	const onPreviewPress = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!canPreview || !isReadyForPreview) return;
		MenuItemAction.createOpenPrototype(frameId).callback();
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const mediaProps = { size, $split: isSplitLayout };

	const renderLoadingIndicator = () => {
		return (
			<S.Loading
				onClick={() => {
					onClickThumbnail();
				}}
				{...mediaProps}
			>
				<Moon progress={moonProgress} size={Math.round(size * 0.42)} isBreathing={isIndeterminate} />
			</S.Loading>
		);
	};

	const renderErrorIndicator = () => {
		return (
			<S.Error
				onClick={() => {
					onClickThumbnail();
				}}
				{...mediaProps}
			>
				<Icon type={IconType.ERROR} width={32} height={32} color={theme.gray[700]} />
			</S.Error>
		);
	};

	const renderFrameImage = () => {
		return (
			<>
				<S.Image
					// this key is important to manage image loading correctly
					key={imageSrc + 'thumbnail'}
					ref={handleImageRef}
					src={imageSrc ?? undefined}
					onClick={() => {
						onClickThumbnail();
					}}
					{...mediaProps}
				/>
				{isHovered && onDelete && (
					<S.Icon>
						<SmallIconButton
							state={SmallIconButtonState.REGULAR}
							iconType={IconType.CLOSE}
							onClick={() => {
								onDelete?.();
							}}
						/>
					</S.Icon>
				)}
			</>
		);
	};

	const renderContents = () => {
		if (!frame) {
			return renderFrameImage();
		} else if (isImageLoading || frame.metadata?.status === FrameStatus.PROCESSING) {
			return renderLoadingIndicator();
		} else if (frame.metadata?.status === FrameStatus.ERROR) {
			return renderErrorIndicator();
		} else {
			return renderFrameImage();
		}
	};

	const renderPreviewBar = () => {
		if (!showPreviewBar) return null;
		const previewDisabled = !isReadyForPreview || !canPreview;
		return (
			<S.PreviewBar
				$disabled={previewDisabled}
				onClick={onPreviewPress}
				data-tooltip-id={previewTooltipId}
				data-tooltip-content={previewDisabled ? 'Preview unavailable' : 'Click to preview'}
			>
				<Icon
					type={IconType.PLAY}
					width={previewIconSize}
					height={previewIconSize}
					color={previewDisabled ? S.PREVIEW_BAR_ICON_COLOR_DISABLED : S.PREVIEW_BAR_ICON_COLOR}
				/>
			</S.PreviewBar>
		);
	};

	const placement: PlacesType = tooltipPlacement ?? 'bottom';

	return (
		<>
			<S.Base
				ref={ref}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				size={size}
				$split={isSplitLayout}
				data-frame-id={frameId}
			>
				{isSplitLayout ? (
					<>
						<S.MediaArea
							$split
							data-tooltip-id={tooltipId}
							data-tooltip-content={tooltipString}
						>
							{renderContents()}
						</S.MediaArea>
						{renderPreviewBar()}
					</>
				) : (
					<a data-tooltip-id={tooltipId} data-tooltip-content={tooltipString}>
						{renderContents()}
					</a>
				)}
			</S.Base>
			<Tooltip id={tooltipId} placement={placement} />
			{showPreviewBar && <Tooltip id={previewTooltipId} placement="top" />}
			{isDeletedLightboxOpen && (
				<DeletedFrameLightbox
					frameId={frameId}
					onClose={() => setIsDeletedLightboxOpen(false)}
				/>
			)}
		</>
	);
};

export default Thumbnail;
